import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import {
  publishedListingResultIds,
  findCategoryPathByMatch,
  categoryPathToLevelParams,
  validatedCategoryParams,
} from '../../util/landingPageListings';
import { createImageVariantConfig } from '../../util/sdkLoader';
import {
  EUROPE_BOUNDS,
  LOUNGE_CATEGORY,
  LOUNGE_MATCHER,
  LOUNGE_CITIES,
  getBoundsForSdk,
} from '../../util/airportLoungeConfig';

// Europe-wide query: only needs to cover grid mode (the page switches to the
// capped featured grid above 18 lounges) plus supply meta.totalItems, so a
// small page suffices. Per-city coverage comes from the city card queries
// below — the old perPage=100 createdAt-sorted page left the top cities
// (London 21, Geneva 5, …) with ZERO cards in the SSR payload.
const RESULT_PAGE_SIZE = 24;

// Cards fetched per city for the featured grid (live counts still come from
// the same responses via meta.totalItems). 12 cities × 3 cards + the Europe
// page ≈ 60 normalised listings — still a slimmer SSR payload than before.
const CITY_CARDS_PER_CITY = 3;

// Resolve the lounge category against the hosted category tree (id/casing varies
// per marketplace, e.g. 'Lounge'); fall back to the literal id. Shared by the
// grid query, the cheapest-price query and the per-city card queries. Also used
// by the page component so every LP → SearchPage link carries the same
// validated category params (never a hardcoded id).
export const resolveLoungeCategoryParams = config => {
  const categories = config.categoryConfiguration?.categories || [];
  const loungePath = findCategoryPathByMatch(
    categories,
    (id, name) => LOUNGE_MATCHER.test(id || '') || LOUNGE_MATCHER.test(name || '')
  );
  return loungePath && loungePath.length
    ? validatedCategoryParams(config, categoryPathToLevelParams(config, loungePath))
    : { pub_categoryLevel1: LOUNGE_CATEGORY };
};

// Shared field/variant config for every query that feeds ListingCards.
const cardQueryFields = config => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return {
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.categoryLevel1',
      'publicData.categoryLevel2',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
      // Lounge-specific: drives the airport pill label fallback and lets the
      // ListingPage OrderPanel surface the LoungePair deeplink automatically.
      'publicData.location',
      'publicData.externalBookingUrl',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
    ],
    // Beide Extended-Data-Felder des Nutzers MUESSEN hier stehen, in jedem
    // Sparse-Fieldset, das Nutzer laedt — nicht nur dort, wo etwas davon sichtbar
    // ist: combinedResourceObjects (util/data.js) ersetzt attributes.profile beim
    // Zusammenfuehren KOMPLETT, und sanitizeUser erzwingt publicData:{} als
    // eigenen Schluessel (Destructuring-Default). Eine sparsame Antwort loescht
    // also die Extended Data einer bereits geladenen vollen Antwort aus dem Store.
    // publicData traegt stripeConnected — die Weiche zwischen Standard-Checkout
    // und Soft-Booking (util/softReservation.js) —, metadata traegt stripeVerified
    // fuer das Badge. Fehlt publicData, landet ein zahlungsbereiter Anbieter im
    // langsameren Soft-Booking.
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData',
      'profile.metadata',
    ],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };
};

const buildSdkParams = config => {
  const bounds = getBoundsForSdk(EUROPE_BOUNDS);
  if (!bounds) {
    return null;
  }

  return {
    bounds,
    // Restrict to lounge listings so the result budget isn't spent on
    // coworking spaces (category resolved against the hosted tree).
    ...resolveLoungeCategoryParams(config),
    page: 1,
    perPage: RESULT_PAGE_SIZE,
    minStock: 1,
    stockMode: 'match-undefined',
    ...cardQueryFields(config),
  };
};

// Single cheapest lounge → drives the hero "from CHF X / visit" chip.
// NOTE: Sharetribe's `price` sort is inverted vs the displayed price for these
// imported lounges (verified live: `sort: '-price'` returns the cheapest first),
// so '-price' + perPage 1 yields the true lowest displayed price.
const cheapestParams = config => {
  const bounds = getBoundsForSdk(EUROPE_BOUNDS);
  if (!bounds) {
    return null;
  }
  return {
    bounds,
    ...resolveLoungeCategoryParams(config),
    page: 1,
    perPage: 1,
    minStock: 1,
    stockMode: 'match-undefined',
    sort: '-price',
    'fields.listing': ['price'],
  };
};

// Per-city card query: a handful of bookable cards per city for the featured
// grid (the Europe page alone has no coverage guarantee for the top cities),
// with the live "(N)" count read from the same response via meta.totalItems.
const cityCardParams = (config, city) => {
  const bounds = getBoundsForSdk(city.bounds);
  if (!bounds) {
    return null;
  }
  return {
    bounds,
    ...resolveLoungeCategoryParams(config),
    page: 1,
    perPage: CITY_CARDS_PER_CITY,
    minStock: 1,
    stockMode: 'match-undefined',
    // Cheapest first (Sharetribe's price sort is inverted for the imported
    // lounges — verified live) so each city's featured cards anchor the
    // "ab CHF X" promise.
    sort: '-price',
    ...cardQueryFields(config),
  };
};

export const fetchLoungeListings = createAsyncThunk(
  'AirportLoungeLandingPage/fetchLoungeListings',
  async ({ config }, thunkAPI) => {
    const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
    const params = buildSdkParams(config);
    if (!params) {
      return rejectWithValue(storableError(new Error('Invalid bounds')));
    }

    try {
      // The Europe grid query and the per-city card queries are all normalised
      // into Redux (they feed ListingCards). The cheapest-price query and the
      // city queries are best-effort side queries that must never fail the
      // page → each catches independently.
      const gridPromise = sdk.listings.query(params);

      const cheapestP = cheapestParams(config);
      const cheapestPromise = cheapestP
        ? sdk.listings.query(cheapestP).catch(() => null)
        : Promise.resolve(null);

      const cityPromises = LOUNGE_CITIES.map(city => {
        const cp = cityCardParams(config, city);
        return cp
          ? sdk.listings
              .query(cp)
              .then(res => ({
                name: city.name,
                count: res?.data?.meta?.totalItems ?? 0,
                response: res,
              }))
              .catch(() => ({ name: city.name, count: 0, response: null }))
          : Promise.resolve({ name: city.name, count: 0, response: null });
      });

      const [response, cheapestRes, ...cityResults] = await Promise.all([
        gridPromise,
        cheapestPromise,
        ...cityPromises,
      ]);

      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(response, { listingFields }));
      cityResults.forEach(r => {
        if (r?.response) {
          dispatch(addMarketplaceEntities(r.response, { listingFields }));
        }
      });

      const cheapest = cheapestRes?.data?.data?.[0]?.attributes?.price || null;
      const lowestPrice =
        cheapest && typeof cheapest.amount === 'number'
          ? { amount: cheapest.amount, currency: cheapest.currency }
          : null;

      const cityCounts = {};
      cityResults.forEach(r => {
        if (r) {
          cityCounts[r.name] = r.count;
        }
      });

      // Merge grid + city ids (deduped, city cards first so the featured grid's
      // guaranteed per-city coverage leads the pool).
      const cityIds = cityResults.flatMap(r =>
        r?.response ? publishedListingResultIds(r.response.data) : []
      );
      const gridIds = publishedListingResultIds(response.data);
      const seen = new Set();
      const ids = [...cityIds, ...gridIds].filter(id => {
        if (seen.has(id.uuid)) {
          return false;
        }
        seen.add(id.uuid);
        return true;
      });

      return {
        ids,
        lowestPrice,
        cityCounts,
        totalLounges: response?.data?.meta?.totalItems ?? null,
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

const initialState = {
  listingIds: [],
  lowestPrice: null,
  cityCounts: {},
  totalLounges: null,
  fetchInProgress: false,
  fetchError: null,
};

const airportLoungeLandingPageSlice = createSlice({
  name: 'AirportLoungeLandingPage',
  initialState,
  reducers: {
    clearLoungeLanding: () => ({ ...initialState }),
  },
  extraReducers: builder => {
    builder
      .addCase(fetchLoungeListings.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchLoungeListings.fulfilled, (state, action) => {
        state.listingIds = action.payload.ids;
        state.lowestPrice = action.payload.lowestPrice;
        state.cityCounts = action.payload.cityCounts || {};
        state.totalLounges = action.payload.totalLounges;
        state.fetchInProgress = false;
      })
      .addCase(fetchLoungeListings.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
        state.listingIds = [];
      });
  },
});

export const { clearLoungeLanding } = airportLoungeLandingPageSlice.actions;

export default airportLoungeLandingPageSlice.reducer;

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchListings =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);

  if (!canFetchListings) {
    return Promise.resolve();
  }

  await dispatch(fetchLoungeListings({ config }));
};
